import { Box, Button, Group, Modal, NumberInput, Stack, Text, TextInput } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import React, { useEffect } from 'react';
import { fundSchema, EditableFund } from '../schemas/fundSchema';
import { AfternoonDuplicateFieldApiError } from 'src/error/ApiError';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { useCreateFund } from 'src/company/hooks/useCreateFund';

type AddFundModalProps = {
  opened: boolean;
  onClose: () => void;
};

const defaultValues: EditableFund = {
  name: '',
  code: '',
  pricePerUnit: 0,
  amcPercentage: 0,
};

export function AddFundModal({ opened, onClose }: AddFundModalProps) {
  const form = useForm<EditableFund>({
    initialValues: defaultValues,
    validate: zodResolver(fundSchema),
    validateInputOnBlur: true,
  });
  const { createFund } = useCreateFund();

  useEffect(() => {
    if (!opened) {
      form.setValues(defaultValues);
    }
  }, [opened]);

  const handleClose = () => {
    form.setValues(defaultValues);
    onClose();
  };

  const handleSubmit = async (values: EditableFund) => {
    try {
      await createFund(values);
      form.setValues(defaultValues);
      onClose();
    } catch (error) {
      if (AfternoonDuplicateFieldApiError.isDuplicateFieldError(error)) {
        form.setFieldError(error.field, error.message);
      } else {
        notifyAndLogError(
          'Error',
          'We were not able to create the fund. Please try again or contact support.',
          error,
        );
      }
    }
  };

  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    form.onSubmit(handleSubmit)(event);
  };

  return (
    <Modal opened={opened} onClose={handleClose} title="Add fund" size="lg">
      <form onSubmit={handleFormSubmit} data-testid="add-fund-modal">
        <Stack gap="xs">
          <TextInput
            label="Fund name"
            labelProps={{ variant: 'light' }}
            {...form.getInputProps('name')}
            required
          />
          <TextInput
            label="Fund code"
            labelProps={{ variant: 'light' }}
            {...form.getInputProps('code')}
            required
          />
          <Group grow>
            <NumberInput
              label="Price per unit"
              labelProps={{ variant: 'light' }}
              {...form.getInputProps('pricePerUnit')}
              leftSection="£"
              required
              hideControls
            />
            <NumberInput
              label="Annual Management Charge"
              labelProps={{ variant: 'light' }}
              {...form.getInputProps('amcPercentage')}
              rightSection={
                <Box>
                  <Text>%</Text>
                </Box>
              }
              required
            />
          </Group>
        </Stack>

        <Group justify="flex-end" mt="xl">
          <Button
            variant="filled-white"
            fullWidth
            type="submit"
            size="xl"
            disabled={!form.isValid()}
          >
            Confirm & add
          </Button>
        </Group>
      </form>
    </Modal>
  );
}
